import { useMemo } from 'react';
import { Quote } from '@/model/quotesModel';
import { useAdjustedQuotes } from '@/features/shop-insurance/hooks/use-adjusted-quotes';
import { useBenefitDesignQuotes } from '@/features/shop-insurance/hooks/use-benefit-design-quotes';
import { useAllPaymentOptionQuotesFetching } from '@/features/shop-insurance/hooks/use-all-payment-option-quotes-fetching';
import { useSelectedFeatureBenefitsFilters } from '@/features/shop-insurance/stores/eligibility-filters-store';
import type {
  BenefitDesignInputs,
  CombinedFilterType,
  ShopInsuranceMode,
  TraditionalLTCQuoteWithROI,
} from '@/features/shop-insurance/types';

interface UseShopInsuranceModeQuotesParams {
  mode: ShopInsuranceMode;
  selectedTab: ShopInsuranceTab;
  client: Client | undefined;
  quotes: Quote[] | undefined;
  allPaymentOptionQuotes: Quote[];
  premiumValue: number;
  benefitDurationValue: CombinedFilterType;
  inflationProtectionValue: CombinedFilterType;
  benefitDesignInputs: BenefitDesignInputs;
  returnOnInvestmentCalculationType: ReturnOnInvestmentCalculationType;
  hasPartnerWithDemographics: boolean;
  filteredTraditionalLTCQuotes: TraditionalLTCQuoteWithROI[];
  isGapFillMode?: boolean;
  totalCost?: number;
}

export function useShopInsuranceModeQuotes({
  mode,
  selectedTab,
  client,
  quotes,
  allPaymentOptionQuotes,
  premiumValue,
  benefitDurationValue,
  inflationProtectionValue,
  benefitDesignInputs,
  returnOnInvestmentCalculationType,
  hasPartnerWithDemographics,
  filteredTraditionalLTCQuotes,
  isGapFillMode,
  totalCost,
}: UseShopInsuranceModeQuotesParams) {
  const selectedFeatureBenefitsFilters = useSelectedFeatureBenefitsFilters();

  useAllPaymentOptionQuotesFetching({
    mode,
    selectedTab,
    hasPartnerWithDemographics,
  });

  const { adjustedQuotes, isAdjusted } = useAdjustedQuotes({
    client,
    selectedTab,
    returnOnInvestmentCalculationType,
    originalQuotes: quotes,
    selectedPremium: premiumValue,
    selectedBenefitDuration: benefitDurationValue,
    selectedInflationProtection: inflationProtectionValue,
    selectedFeatureBenefitsFilters,
    isGapFillMode,
    totalCost,
  });

  const { quoteGroups: benefitDesignQuoteGroups, availableOptions: benefitDesignAvailableOptions } =
    useBenefitDesignQuotes({
      originalQuotes: allPaymentOptionQuotes.length ? allPaymentOptionQuotes : quotes,
      client,
      selectedTab,
      inputs: benefitDesignInputs,
      returnOnInvestmentCalculationType,
    });

  const benefitDesignFlatQuotes = useMemo(
    () => benefitDesignQuoteGroups.map(group => group.representativeQuote),
    [benefitDesignQuoteGroups],
  );

  const modeAdjustedQuotes = mode === 'benefit-design' ? benefitDesignFlatQuotes : adjustedQuotes;
  const modeTraditionalLTCQuotes = mode === 'benefit-design' ? [] : filteredTraditionalLTCQuotes;

  return {
    adjustedQuotes,
    isAdjusted,
    benefitDesignQuoteGroups,
    benefitDesignAvailableOptions,
    modeAdjustedQuotes,
    modeTraditionalLTCQuotes,
    selectedFeatureBenefitsFilters,
  };
}
